import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useAuth } from "../../auth/AuthContext";
import { listAlumnosByApoderado, listCuotasByApoderado } from "../../lib/data";
import { formatCLP, formatFecha } from "../../lib/format";
import { MESES_ES, type Alumno, type Cuota } from "../../lib/types";
import { EstadoBadge, Spinner, StatCard } from "../../components/ui";

export function PortalApoderado() {
  const { profile, logout } = useAuth();
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  const [cuotas, setCuotas] = useState<Cuota[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profile?.apoderadoId) return;
    (async () => {
      try {
        const [a, c] = await Promise.all([
          listAlumnosByApoderado(profile.apoderadoId!),
          listCuotasByApoderado(profile.apoderadoId!),
        ]);
        setAlumnos(a);
        setCuotas(c.sort((x, y) => y.ano - x.ano || x.mes - y.mes));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.apoderadoId]);

  const kpis = useMemo(() => {
    const pagado = cuotas.filter((c) => c.estado === "pagado").reduce((s, c) => s + c.montoFinal, 0);
    const deuda = cuotas.filter((c) => ["pendiente", "por_vencer", "abono"].includes(c.estado)).reduce((s, c) => s + c.montoFinal, 0);
    const vencidas = cuotas.filter((c) => c.estado === "pendiente").length;
    return { pagado, deuda, vencidas };
  }, [cuotas]);

  const porMes = useMemo(() => {
    const map = new Map<number, { mes: string; pagado: number; pendiente: number }>();
    for (const c of cuotas) {
      const key = c.mes;
      if (!map.has(key)) map.set(key, { mes: MESES_ES[c.mes - 1].slice(0, 3), pagado: 0, pendiente: 0 });
      if (c.estado === "pagado") map.get(key)!.pagado += c.montoFinal;
      else map.get(key)!.pendiente += c.montoFinal;
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
  }, [cuotas]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-600 text-lg">🏐</span>
            <div>
              <p className="font-bold leading-tight text-slate-800">Mi cuenta</p>
              <p className="text-xs text-slate-400">{profile?.nombre}</p>
            </div>
          </div>
          <button className="btn-ghost" onClick={() => logout()}>Salir</button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        {loading ? (
          <Spinner label="Cargando tu cuenta…" />
        ) : error ? (
          <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Al día (pagado)" value={formatCLP(kpis.pagado)} tone="emerald" />
              <StatCard label="Por pagar" value={formatCLP(kpis.deuda)} tone="amber" />
              <StatCard label="Vencidas" value={String(kpis.vencidas)} tone={kpis.vencidas ? "rose" : "slate"} />
            </div>

            {alumnos.length > 0 && (
              <div className="card p-4">
                <h2 className="mb-2 font-bold text-slate-700">Mis alumnos</h2>
                <div className="flex flex-wrap gap-2">
                  {alumnos.map((a) => (
                    <span key={a.id} className="badge bg-brand-50 text-brand-700">{a.nombre}</span>
                  ))}
                </div>
              </div>
            )}

            {porMes.length > 0 && (
              <div className="card p-4">
                <h2 className="mb-3 font-bold text-slate-700">Tus cuotas por mes</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={porMes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip formatter={(v: number) => formatCLP(v)} />
                    <Bar dataKey="pagado" name="Pagado" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="pendiente" name="Pendiente" stackId="a" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="card overflow-x-auto">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="font-bold text-slate-700">Detalle de cuotas</h2>
              </div>
              {cuotas.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Aún no hay cuotas registradas.</p>
              ) : (
                <table className="w-full">
                  <thead className="border-b border-slate-100">
                    <tr>
                      <th className="th">Alumno</th><th className="th">Mes</th><th className="th">Práctica</th>
                      <th className="th">Vence</th><th className="th">Total</th><th className="th">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {cuotas.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50">
                        <td className="td font-medium">{c.alumnoNombre}</td>
                        <td className="td">{MESES_ES[c.mes - 1]}</td>
                        <td className="td">{c.practica === "volleyball" ? "Vóleibol" : "Fútbol"}</td>
                        <td className="td">{formatFecha(c.fechaVencimiento)}</td>
                        <td className="td font-semibold">{formatCLP(c.montoFinal)}</td>
                        <td className="td"><EstadoBadge estado={c.estado} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="text-center text-xs text-slate-400">
              Para transferir, usa la cuenta indicada en cada cuota. Si un pago no aparece, contacta a la administración.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
